import { useCallback, useRef, useState } from 'react';
import { ClipId, TimeMs } from '../types';
import { DEFAULT_SNAP_THRESHOLD_PX, msToPx, pxToMs, snapEdge, SnapTarget } from '../utils/snap';

export type DragMode = 'move' | 'resize-start' | 'resize-end';

/** Lightweight snapshot for visual preview during a drag. */
export type DragSnapshot = {
  clipId: ClipId;
  mode: DragMode;
  offsetPx: number;
  /** Px position of the active snap target, if any. Used to render a guide line. */
  snapPx: number | null;
};

export type DragCommit = {
  clipId: ClipId;
  mode: DragMode;
  newStartMs?: TimeMs;
  newDurationMs?: TimeMs;
};

type Internal = {
  clipId: ClipId;
  mode: DragMode;
  offsetPx: number;
  snapPx: number | null;
  originX: number;
  originStartMs: TimeMs;
  originDurationMs: TimeMs;
};

type UseClipDragOptions = {
  pxPerSecond: number;
  startOffset: number;
  onCommit: (commit: DragCommit) => void;
  /** Lazy provider so playhead (high-frequency) can be included without re-creating the hook. */
  getSnapTargets?: () => SnapTarget[];
};

const MIN_DURATION = 50;

/** Px position of the edge we're snapping, given the current drag state. */
const computeEdgePx = (cur: Internal, pxPerSecond: number, startOffset: number, offsetPx: number): number => {
  switch (cur.mode) {
    case 'move': {
      const startPx = msToPx(cur.originStartMs, pxPerSecond, startOffset);
      return startPx + offsetPx;
    }
    case 'resize-start': {
      const startPx = msToPx(cur.originStartMs, pxPerSecond, startOffset);
      return startPx + offsetPx;
    }
    case 'resize-end': {
      const endPx = msToPx(cur.originStartMs + cur.originDurationMs, pxPerSecond, startOffset);
      return endPx + offsetPx;
    }
  }
};

/**
 * Drag/resize gesture handler for a single clip at a time.
 *
 * - Single `onCommit` per drag (one undo entry).
 * - Listeners bound to `window` so the gesture survives the cursor leaving the element.
 * - If `getSnapTargets` is provided, edges snap to the nearest target within threshold,
 *   excluding the dragging clip itself.
 */
export const useClipDrag = ({ pxPerSecond, startOffset, onCommit, getSnapTargets }: UseClipDragOptions) => {
  const [snapshot, setSnapshot] = useState<DragSnapshot | null>(null);
  const internalRef = useRef<Internal | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const startDrag = useCallback(
    (e: React.MouseEvent, clipId: ClipId, startMs: TimeMs, durationMs: TimeMs, mode: DragMode): void => {
      e.stopPropagation();
      e.preventDefault();

      cleanupRef.current?.();

      const initial: Internal = {
        clipId,
        mode,
        offsetPx: 0,
        snapPx: null,
        originX: e.clientX,
        originStartMs: startMs,
        originDurationMs: durationMs
      };
      internalRef.current = initial;
      setSnapshot({ clipId, mode, offsetPx: 0, snapPx: null });

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize';
      document.body.style.userSelect = 'none';

      const handleMove = (ev: MouseEvent) => {
        const cur = internalRef.current;
        if (!cur) return;
        const rawOffsetPx = ev.clientX - cur.originX;

        let offsetPx = rawOffsetPx;
        let snapPx: number | null = null;

        if (getSnapTargets) {
          const edgePx = computeEdgePx(cur, pxPerSecond, startOffset, rawOffsetPx);
          const result = snapEdge(edgePx, getSnapTargets(), cur.clipId, DEFAULT_SNAP_THRESHOLD_PX);
          if (result.snapped) {
            offsetPx = rawOffsetPx + (result.snappedPx - edgePx);
            snapPx = result.targetPx;
          }
        }

        cur.offsetPx = offsetPx;
        cur.snapPx = snapPx;
        setSnapshot({ clipId: cur.clipId, mode: cur.mode, offsetPx, snapPx });
      };

      // Forward-declared so `cleanup` can reference `handleUp` (which itself calls `cleanup`).
      let handleUp: () => void;

      const cleanup = () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        internalRef.current = null;
        cleanupRef.current = null;
        setSnapshot(null);
      };

      handleUp = () => {
        const cur = internalRef.current;
        if (cur && pxPerSecond > 0 && cur.offsetPx !== 0) {
          const deltaMs = pxToMs(cur.offsetPx, pxPerSecond, 0); // 0 = relative delta
          let newStartMs: TimeMs | undefined;
          let newDurationMs: TimeMs | undefined;

          switch (cur.mode) {
            case 'move':
              newStartMs = Math.max(0, cur.originStartMs + deltaMs);
              break;
            case 'resize-end':
              newDurationMs = Math.max(MIN_DURATION, cur.originDurationMs + deltaMs);
              break;
            case 'resize-start':
              newDurationMs = Math.max(MIN_DURATION, cur.originDurationMs - deltaMs);
              break;
          }

          onCommit({ clipId: cur.clipId, mode: cur.mode, newStartMs, newDurationMs });
        }
        cleanup();
      };

      cleanupRef.current = cleanup;
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [pxPerSecond, startOffset, onCommit, getSnapTargets]
  );

  return { snapshot, startDrag };
};
